import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, X, AlertTriangle, CalendarPlus } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SuiteButton } from "@/components/suite";
import { MONTHS_IT } from "@/lib/constants";
import { formatDateIT, fmtHours } from "@/foundation/formatting";
import { formatCounterHours } from "@/lib/counterHours";

const vacation = (p) => (p.vacation_unit === "hours"
  ? `${fmtHours(p.vacation_monthly || 0)} h`
  : `${Number(p.vacation_monthly || 0).toFixed(2)} gg`);

const monthLabel = (m) => `${MONTHS_IT[m.month - 1]} ${m.year}`;

const ProfileValues = ({ p, testid }) => (
  <div className="grid grid-cols-3 gap-2 text-xs" data-testid={testid}>
    <div><div className="text-suite-neutral-500">Ferie</div><div className="font-mono">{vacation(p)}</div></div>
    <div><div className="text-suite-neutral-500">ROL</div><div className="font-mono">{formatCounterHours(p.rol_monthly_hours || 0)} h</div></div>
    <div><div className="text-suite-neutral-500">Ex festività</div><div className="font-mono">{formatCounterHours(p.ex_fest_monthly_hours || 0)} h</div></div>
  </div>
);

const Effect = ({ check }) => {
  const until = check.affected_until ? ` fino al ${formatDateIT(check.affected_until)} (escluso)` : " in poi";
  return (
    <div className="text-xs text-suite-neutral-700" data-testid="accrual-delete-effect">
      Periodo interessato: dal <b>{formatDateIT(check.affected_from)}</b>{until}.{" "}
      {check.previous_profile
        ? <>Tornerà valida la decorrenza del <b>{formatDateIT(check.previous_profile.effective_from)}</b>.</>
        : <>Nessuna decorrenza precedente: in quel periodo non maturerà alcun rateo.</>}
      {check.independent_consolidated_months?.length > 0 && (
        <div className="mt-1.5" data-testid="accrual-delete-independent">
          Periodi consolidati prima dell&apos;inserimento della decorrenza, senza dipendenze: {check.independent_consolidated_months.map(monthLabel).join(", ")}.
          L&apos;eliminazione ripristina i valori in vigore al loro consolidamento.
        </div>
      )}
    </div>
  );
};

const Blocked = ({ check }) => (
  <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1" data-testid="accrual-delete-blocked">
    <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={14} /> Eliminazione non consentita</div>
    <div>{check.reason}</div>
    {check.consolidated_months.length > 0 && (
      <div data-testid="accrual-delete-consolidated">
        Dipendenze effettive: {check.consolidated_months.map((m) => `${monthLabel(m)} (${m.dependencies.map((d) => `${d.source}: ${d.why}`).join("; ")})`).join(" · ")}
      </div>
    )}
    {check.corrective_suggestion ? (
      <div>Puoi registrare una decorrenza correttiva dal <b>{formatDateIT(check.corrective_suggestion.effective_from)}</b>, primo mese modificabile: i periodi consolidati restano invariati.</div>
    ) : check.consolidated_months.length > 0 && (
      <div>Il periodo coperto da questa decorrenza è interamente consolidato: non è possibile alcuna rettifica senza riaprire i periodi.</div>
    )}
  </div>
);

export function AccrualDeleteDialog({ employeeId, employeeName, row, onClose, onDeleted, onCorrective }) {
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!row) { setCheck(null); return; }
    api.get(`/employees/${employeeId}/accrual-profiles/${row.id}/delete-check`)
      .then(({ data }) => setCheck(data))
      .catch((e) => { toast.error(apiErr(e)); onClose(); });
  }, [row, employeeId, onClose]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.delete(`/employees/${employeeId}/accrual-profiles/${row.id}`);
      toast.success("Decorrenza eliminata; operazione registrata nell'audit");
      onDeleted();
    } catch (e) {
      const blocked = e?.response?.data?.detail?.check;
      if (blocked) setCheck(blocked);
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="accrual-delete-dialog">
        <DialogHeader>
          <DialogTitle>Elimina decorrenza</DialogTitle>
          <DialogDescription className="text-xs">
            Dipendente: <b data-testid="accrual-delete-employee">{employeeName}</b> · Decorrenza del{" "}
            <b data-testid="accrual-delete-date">{row ? formatDateIT(row.effective_from) : ""}</b>
          </DialogDescription>
        </DialogHeader>
        {!check ? (
          <div className="py-4 text-xs text-suite-neutral-500" data-testid="accrual-delete-loading">Verifica degli effetti in corso…</div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="rounded-md border border-suite-neutral-200 p-3">
              <div className="text-[11px] uppercase tracking-wider text-suite-neutral-500 font-semibold mb-1.5">Valori che verranno rimossi</div>
              <ProfileValues p={check.profile} testid="accrual-delete-values" />
            </div>
            {check.previous_profile && (
              <div className="rounded-md border border-suite-neutral-200 p-3">
                <div className="text-[11px] uppercase tracking-wider text-suite-neutral-500 font-semibold mb-1.5">Valori che torneranno validi</div>
                <ProfileValues p={check.previous_profile} testid="accrual-delete-restored-values" />
              </div>
            )}
            {check.allowed ? <Effect check={check} /> : <Blocked check={check} />}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="accrual-delete-cancel-btn">
            <X size={14} className="mr-1" /> Annulla
          </Button>
          {check?.allowed && (
            <SuiteButton onClick={confirm} icon={Trash2} loading={busy}
              className="!bg-suite-error hover:!bg-suite-error/90" data-testid="accrual-delete-confirm-btn">
              Conferma eliminazione
            </SuiteButton>
          )}
          {check && !check.allowed && check.corrective_suggestion && (
            <SuiteButton onClick={() => onCorrective(check.corrective_suggestion)} icon={CalendarPlus}
              data-testid="accrual-delete-corrective-btn">
              Crea decorrenza correttiva
            </SuiteButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
